import React, { forwardRef, IframeHTMLAttributes, useMemo } from 'react';

export type EmbedProps = {
	embedType: 'youtube' | 'spotify' | 'twentythree' | 'vimeo';
	src: string;
	inline?: boolean;
} & IframeHTMLAttributes<HTMLIFrameElement>;

export default forwardRef<HTMLIFrameElement, EmbedProps>(function Embed(
	{ embedType, src, inline = false, ...props },
	ref
) {
	const href = useMemo(() => {
		let url: URL | undefined;
		try {
			url = new URL(src, 'https://www.smukfest.dk');
		} catch (e) {
			console.error(e);
		}

		if (!url) return;

		if (embedType === 'spotify') {
			url.pathname = '/embed/' + url.pathname;
			url.searchParams.set('utm_source', 'generator');
		} else if (embedType === 'youtube') {
			let youtubeVideoId = '';
			if (url.pathname === '/watch' && url.searchParams.has('v')) {
				youtubeVideoId = url.searchParams.get('v') ?? '';
				url.searchParams.delete('v');
			} else if (url.pathname === '/embed') {
				youtubeVideoId = url.pathname.split('/').pop();
			}
			url.hostname = 'youtube.com';
			url.pathname = '/embed/' + youtubeVideoId;
			url.searchParams.set('modestbranding', '1');
			url.searchParams.set('origin', 'https://www.smukfest.dk');
			url.searchParams.set('rel', '0');
			url.searchParams.set('iv_load_policy', '3');
			url.searchParams.set('playsinline', '1');
			url.searchParams.set('color', 'white');

			if (inline) {
				url.searchParams.set('autoplay', '1');
				url.searchParams.set('muted', '1');
				url.searchParams.set('controls', '0');
				url.searchParams.set('disablekb', '1');
				url.searchParams.set('loop', '1');
				url.searchParams.set('fs', '0');
				url.searchParams.set('mute', '1');
				url.searchParams.set('playlist', youtubeVideoId || '');
			}

			// url.search = '?' + params.toString();
		} else if (embedType === 'twentythree') {
			url.searchParams.set('ambient', '1');
			url.searchParams.set('defaultQuality', 'fullhd');

			if (inline) {
				url.searchParams.set('showDescriptions', '0');
				url.searchParams.set('showLogo', '0');
				url.searchParams.set('hideBigPlay', '1');
				url.searchParams.set('socialSharing', '0');
				url.searchParams.set('showBrowse', '0');
				url.searchParams.set('loop', '1');
				url.searchParams.set('showTray', '0');
				url.searchParams.set('autoPlay', '1');
				url.searchParams.set('mutedAutoPlay', '1');
				url.searchParams.set('autoMute', '1');
			}
		} else if (embedType === 'vimeo') {
			url.host = 'player.vimeo.com';
			url.pathname = '/video' + url.pathname;
			url.searchParams.set('transparent', '0');

			if (inline) {
				url.searchParams.set('controls', '0');
				url.searchParams.set('autoplay', '1');
				url.searchParams.set('loop', '1');
				url.searchParams.set('muted', '1');
				url.searchParams.set('playsinline', '1');
			}
		}

		return url.href;
	}, [embedType, src, inline]);

	const allow = useMemo(() => {
		if (embedType === 'spotify') {
			return 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
		} else if (embedType === 'youtube') {
			return 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
		} else if (embedType === 'twentythree') {
			return 'autoplay; fullscreen';
		} else if (embedType === 'vimeo') {
			return 'accelerometer; fullscreen; autoplay; encrypted-media; gyroscope; picture-in-picture';
		}
	}, [embedType]);

	return (
		<iframe
			ref={ref}
			src={href}
			width='100%'
			height='100%'
			frameBorder='0'
			loading='lazy'
			allowFullScreen={embedType !== 'spotify'}
			allow={allow}
			referrerPolicy='strict-origin-when-cross-origin'
			{...props}
		></iframe>
	);
});
